// RainViewer radar tiles + a dark basemap. RainViewer is free, keyless, CORS-open
// and browser-friendly (MRMS/own-radar can swap in later behind this same shape).
//
//   index:  GET https://api.rainviewer.com/public/weather-maps.json
//   tile:   {host}{path}/{size}/{z}/{x}/{y}/{color}/{smooth}_{snow}.png

export interface RadarFrame {
  /** Unix seconds. */
  time: number
  /** Path segment, e.g. "/v2/radar/6547d525c13c". */
  path: string
  /** True for forecast (nowcast) frames vs observed past frames. */
  nowcast: boolean
}

export interface RadarIndex {
  host: string
  frames: RadarFrame[]
  generated: number
}

interface RainViewerResponse {
  generated: number
  host: string
  radar?: {
    past?: { time: number; path: string }[]
    nowcast?: { time: number; path: string }[]
  }
}

const RAINVIEWER_INDEX = 'https://api.rainviewer.com/public/weather-maps.json'

export async function fetchRadarIndex(): Promise<RadarIndex> {
  const res = await fetch(RAINVIEWER_INDEX, { headers: { Accept: 'application/json' } })
  if (!res.ok) throw new Error(`RainViewer ${res.status}`)
  const data = (await res.json()) as RainViewerResponse
  const past = (data.radar?.past ?? []).map((f) => ({ ...f, nowcast: false }))
  const nowcast = (data.radar?.nowcast ?? []).map((f) => ({ ...f, nowcast: true }))
  return { host: data.host, generated: data.generated, frames: [...past, ...nowcast] }
}

// NEXRAD Level-III color scheme (US-radar look) + smoothed, with the snow layer on.
const COLOR_SCHEME = 6
const OPTIONS = '1_1'

export function radarTileUrl(
  host: string,
  framePath: string,
  t: { z: number; x: number; y: number },
  size: 256 | 512 = 256,
): string {
  return `${host}${framePath}/${size}/${t.z}/${t.x}/${t.y}/${COLOR_SCHEME}/${OPTIONS}.png`
}

/** Mapbox raster-source `{z}/{x}/{y}` tile TEMPLATE for a frame (the fallback radar source). */
export function radarTileTemplate(host: string, framePath: string, size: 256 | 512 = 256): string {
  return `${host}${framePath}/${size}/{z}/{x}/{y}/${COLOR_SCHEME}/${OPTIONS}.png`
}
